import { Trash2 } from "lucide-react";
import type { Node, Edge } from "@xyflow/react";
import { TriggerConfig } from "./config/trigger-config";
import { AgentConfig } from "./config/agent-config";
import { ConditionConfig } from "./config/condition-config";
import { ActionConfig } from "./config/action-config";

/** One selectable prompt-template variable. */
export interface PromptVariable {
  value: string;
  label: string;
  hint?: string;
}

/**
 * Variables available to a node's prompt template: the workflow input, plus
 * upstream node outputs. When node JSON declares an outputSchema, show exact
 * variables; otherwise keep the editable scaffold.
 */
function upstreamVariables(nodeId: string, nodes: Node[], edges: Edge[]): PromptVariable[] {
  const sourcesByTarget = new Map<string, string[]>();
  for (const e of edges) {
    const list = sourcesByTarget.get(e.target) ?? [];
    list.push(e.source);
    sourcesByTarget.set(e.target, list);
  }
  const upstream = new Set<string>();
  const queue = [...(sourcesByTarget.get(nodeId) ?? [])];
  while (queue.length) {
    const id = queue.shift()!;
    if (upstream.has(id)) continue;
    upstream.add(id);
    for (const s of sourcesByTarget.get(id) ?? []) queue.push(s);
  }
  const vars: PromptVariable[] = [
    { value: "{{input}}", label: "input", hint: "Workflow trigger input" },
  ];
  for (const n of nodes) {
    if (!upstream.has(n.id)) continue;
    const label = nodeLabel(n);
    const outputKeys = outputKeysForNode(n);
    if (outputKeys.length > 0) {
      for (const key of outputKeys) {
        vars.push({
          value: `{{steps.${n.id}.outputs.${key}}}`,
          label: `${label}.${key}`,
          hint: `From ${label}`,
        });
      }
      continue;
    }
    vars.push({
      value: `{{steps.${n.id}.outputs.}}`,
      label,
      hint: `steps.${n.id}.outputs.<key>`,
    });
  }
  return vars;
}

function nodeLabel(node: Node): string {
  const data = (node.data ?? {}) as Record<string, unknown>;
  if (typeof data.label === "string" && data.label.trim()) return data.label.trim();
  if (typeof node.type === "string" && node.type.trim()) return node.type;
  return node.id;
}

function outputKeysForNode(node: Node): string[] {
  const data = (node.data ?? {}) as Record<string, unknown>;
  const schema = data.outputSchema;
  if (!schema || typeof schema !== "object") return [];
  const properties = (schema as { properties?: unknown }).properties;
  if (!properties || typeof properties !== "object" || Array.isArray(properties)) {
    return [];
  }
  return Object.keys(properties).sort();
}

/**
 * Right-rail config panel. Dispatches to the right per-type form based on
 * the selected node's `type`. When no node is selected, shows a placeholder.
 *
 * Admins edit, viewers see the same form in read-only mode.
 */
export function ConfigPanel({
  node,
  nodes,
  edges,
  onChange,
  onDelete,
  readOnly,
  workflowId,
}: {
  node: Node | null;
  nodes: Node[];
  edges: Edge[];
  onChange: (data: Record<string, unknown>) => void;
  onDelete: () => void;
  readOnly: boolean;
  workflowId: string;
}) {
  if (!node) {
    return (
      <div className="flex w-[320px] shrink-0 items-center justify-center border-l border-border bg-surface p-6 text-center">
        <p className="text-xs text-text-muted">
          Select a node to configure it, or drag a new one from the left rail.
        </p>
      </div>
    );
  }

  const data = (node.data ?? {}) as Record<string, unknown>;
  const variables = upstreamVariables(node.id, nodes, edges);

  return (
    <div className="flex w-[320px] shrink-0 flex-col border-l border-border bg-surface">
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {node.type} node
        </p>
        {!readOnly && (
          <button
            onClick={onDelete}
            aria-label="Delete node"
            className="flex h-7 w-7 items-center justify-center rounded text-text-muted transition-colors hover:bg-red-500/10 hover:text-red-500"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {node.type === "trigger" && (
          <TriggerConfig
            data={data}
            onChange={onChange}
            readOnly={readOnly}
            workflowId={workflowId}
          />
        )}
        {node.type === "agent" && (
          <AgentConfig
            data={data}
            onChange={onChange}
            readOnly={readOnly}
            variables={variables}
          />
        )}
        {node.type === "condition" && (
          <ConditionConfig
            data={data}
            onChange={onChange}
            readOnly={readOnly}
            variables={variables}
          />
        )}
        {node.type === "action" && (
          <ActionConfig
            data={data}
            onChange={onChange}
            readOnly={readOnly}
            variables={variables}
          />
        )}
      </div>
      {readOnly && (
        <div className="border-t border-border bg-background/50 px-4 py-2">
          <p className="text-[11px] text-text-muted">
            Only tenant admins can edit workflows.
          </p>
        </div>
      )}
    </div>
  );
}
